import { useCallback, useEffect, useMemo, useState } from "react";

import { useTheme } from "styled-components";

import stopEvents from "Utilities/stopEvents";
import truncate from "Utilities/truncate";

import { useFnrUploadButtonContext } from "./FnrUploadButton";

const useExtendedOnChange = () => {
  const { setHasValue, setDisplayName, onChange } = useFnrUploadButtonContext();
  const extendedOnChange = useCallback(
    async ({
      target: {
        files: [file],
      },
    }) => {
      if (!file) return;
      const fileNameOrFalse = await onChange(file);
      if (fileNameOrFalse === false) return;
      setHasValue(true);
      setDisplayName(fileNameOrFalse || file.name);
    },
    [onChange, setDisplayName, setHasValue]
  );

  return extendedOnChange;
};

const useClear = () => {
  const { onChange, setHasValue, onDelete, containerRef, inputRef } =
    useFnrUploadButtonContext();

  const clear = useCallback(
    (e) => {
      stopEvents(e);
      if (typeof onDelete === "function") onDelete();
      onChange(undefined);
      setHasValue(false);
      inputRef.current.value = "";
      containerRef.current.focus();
    },
    [onDelete, onChange, setHasValue, inputRef, containerRef]
  );

  return clear;
};

const useWithValueText = () => {
  const { displayName, initValue, compact } = useFnrUploadButtonContext();

  const withValueText = useMemo(() => {
    const value = displayName || initValue;
    if (compact) return truncate(value, { length: 14, breakWord: true });
    return value;
  }, [compact, displayName, initValue]);

  return withValueText;
};

const useWithoutValueText = () => {
  const { documentName, compact, readOnly } = useFnrUploadButtonContext();
  const isUploading = useIsUploading();

  const withoutValueText = useMemo(() => {
    const uploadTensed = isUploading ? "Uploading" : "Upload";
    const elispis = isUploading ? "..." : "";
    if (readOnly) return "";
    if (compact) return `${uploadTensed} File${elispis}`;
    return `${uploadTensed} ${documentName}${elispis}`;
  }, [isUploading, readOnly, compact, documentName]);

  return withoutValueText;
};

const useShowDelete = () => {
  const isUploading = useIsUploading();

  const { hasValue, isHoverFocused, readOnly } = useFnrUploadButtonContext();
  return Boolean(hasValue && isHoverFocused && !readOnly && !isUploading);
};

const useShowEditButton = () => {
  const isUploading = useIsUploading();
  const { hasValue, isHoverFocused, readOnly, onEditClick } =
    useFnrUploadButtonContext();
  return Boolean(
    hasValue && isHoverFocused && readOnly && !isUploading && onEditClick
  );
};

const usePercAsNumber = () => {
  const { hasValue, progressPercentage } = useFnrUploadButtonContext();

  const [show100Perc, setShow100Perc] = useState(false);

  // If hasValue changes one way or the other,the progress bar should be reset.
  useEffect(() => {
    setShow100Perc(false);
  }, [hasValue]);

  const percAsNumber = useMemo(() => {
    const perc = Math.round(Number(progressPercentage) * 100);
    if (show100Perc) return perc;
    return perc % 100;
  }, [progressPercentage, show100Perc]);

  // if the percentage is not 100, it should display 100 when it gets there.
  useEffect(() => {
    if (percAsNumber !== 100 && percAsNumber !== 0) {
      setShow100Perc(true);
    }
  }, [percAsNumber]);

  return percAsNumber;
};

const useIsUploading = () => {
  const percAsNumber = usePercAsNumber();

  return Boolean(percAsNumber % 100);
};

const transitionPercWidth = 2; // percent
const useProgressGradientString = () => {
  const {
    palette: { lightBlue },
  } = useTheme();
  const percAsNumber = usePercAsNumber();

  const percString1 = `${Math.max(percAsNumber - transitionPercWidth, 0)}%`;
  const percString2 = `${percAsNumber}%`;

  const progressGradientString = `linear-gradient(to right, ${lightBlue} ${percString1}, transparent ${percString2})`;
  return progressGradientString;
};

export {
  useExtendedOnChange,
  useClear,
  useWithValueText,
  useWithoutValueText,
  useShowDelete,
  useProgressGradientString,
  useIsUploading,
  useShowEditButton,
};
